import React from 'react';

const SectionHeader = ({ num, tag, title, color = '#F97316' }) => (
  <div style={{ marginBottom: '36px', position: 'relative' }}>
    <div
      style={{
        fontFamily: 'Impact, sans-serif',
        fontSize: 'clamp(4rem, 10vw, 8rem)',
        fontWeight: 900,
        color: `${color}08`,
        lineHeight: 1,
        position: 'absolute',
        top: '-20px',
        left: '-10px',
        userSelect: 'none',
        letterSpacing: '-0.04em'
      }}
    >
      {num}
    </div>
    <div style={{ position: 'relative', zIndex: 1 }}>
      <div
        style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: '0.6875rem',
          color: color,
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          marginBottom: '8px'
        }}
      >
        {tag}
      </div>
      <h2
        style={{
          fontFamily: 'Impact, sans-serif',
          fontSize: 'clamp(1.5rem, 3.5vw, 2.75rem)',
          fontWeight: 900,
          color: '#EEF0F2',
          textTransform: 'uppercase',
          letterSpacing: '-0.02em',
          lineHeight: 1
        }}
      >
        {title}
      </h2>
    </div>
  </div>
);

const BodyText = ({ children }) => (
  <p
    style={{
      fontFamily: 'Georgia, serif',
      fontSize: '1rem',
      color: 'rgba(238,240,242,0.72)',
      lineHeight: 1.85,
      marginBottom: '20px',
      maxWidth: '760px'
    }}
  >
    {children}
  </p>
);

const PolaroidCallout = ({ title, children, accent = '#F97316' }) => (
  <div
    style={{
      background: `${accent}08`,
      border: `1px solid ${accent}25`,
      borderLeft: `4px solid ${accent}`,
      padding: '24px 28px',
      marginBottom: '24px',
      position: 'relative',
      transform: 'rotate(-0.3deg)'
    }}
  >
    <div
      style={{
        fontFamily: 'Syne, sans-serif',
        fontSize: '0.75rem',
        fontWeight: 700,
        color: accent,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        marginBottom: '10px'
      }}
    >
      {title}
    </div>
    <div
      style={{
        fontFamily: 'Georgia, serif',
        fontSize: '0.9375rem',
        color: 'rgba(238,240,242,0.75)',
        lineHeight: 1.75
      }}
    >
      {children}
    </div>
  </div>
);

const Divider = ({ color = '#F97316' }) => (
  <div style={{ margin: '48px 0', display: 'flex', alignItems: 'center', gap: '16px' }}>
    <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
    <div style={{ width: '8px', height: '8px', background: color, transform: 'rotate(45deg)' }} />
    <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
  </div>
);

const AcceptanceServiceSection = () => {
  return (
    <>
      {/* Section 1: Acceptance */}
      <section id="aksept" style={{ marginBottom: '80px' }}>
        <SectionHeader
          num="01"
          tag="§ 1 — AKSEPT AV VILKÅR"
          title="Aksept av vilkår"
          color="#F97316"
        />

        <BodyText>
          Ved å bruke NordPass sine tjenester, opprette en konto, eller klikke «Jeg godtar», bekrefter du at du har lest, forstått og godtatt disse vilkårene og betingelsene i sin helhet. Disse vilkårene utgjør en juridisk bindende avtale mellom deg («Bruker») og NordPass AS («NordPass», «vi», «oss»).
        </BodyText>

        <BodyText>
          Dersom du ikke godtar disse vilkårene, må du umiddelbart slutte å bruke tjenestene. Fortsatt bruk av tjenestene etter at endringer er publisert, anses som aksept av de oppdaterte vilkårene.
        </BodyText>

        <PolaroidCallout title="Viktig — Juridisk bindende avtale" accent="#F97316">
          Disse vilkårene er juridisk bindende. Vi anbefaler at du leser dem nøye og tar vare på en kopi. Dersom du er under 18 år, må du ha samtykke fra en forelder eller verge for å bruke tjenestene.
        </PolaroidCallout>

        <div
          style={{
            background: 'rgba(59,111,212,0.06)',
            border: '1px solid rgba(59,111,212,0.2)',
            padding: '20px 24px',
            marginTop: '8px'
          }}
        >
          <p
            style={{
              fontFamily: 'Georgia, serif',
              fontStyle: 'italic',
              fontSize: '0.9375rem',
              color: 'rgba(238,240,242,0.6)',
              lineHeight: 1.7,
              margin: 0
            }}
          >
            <strong style={{ color: '#3B6FD4', fontStyle: 'normal' }}>Sist oppdatert:</strong>{' '}
            Disse vilkårene ble sist oppdatert 1. februar 2026 og erstatter alle tidligere versjoner.{' '}
            <a
              href="/privacy-policy-personvern"
              style={{ color: '#3B6FD4', textDecoration: 'underline', textDecorationColor: 'rgba(59,111,212,0.4)', fontStyle: 'normal' }}
            >
              Se vår Personvernerklæring for informasjon om databehandling →
            </a>
          </p>
        </div>
      </section>

      <Divider color="#F97316" />

      {/* Section 2: Service Description */}
      <section id="tjenester" style={{ marginBottom: '80px' }}>
        <SectionHeader
          num="02"
          tag="§ 2 — TJENESTEBESKRIVELSE"
          title="Tjenestebeskrivelse"
          color="#3B6FD4"
        />

        <BodyText>
          NordPass AS tilbyr en digital plattform for norske sjåfører og transportbrukere, som inkluderer bomkalkulator, interaktivt veikart, kjøretøyadministrasjon og abonnementsbaserte tjenester for veirelatert informasjon i Norge.
        </BodyText>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '28px' }}>
          {[
            { title: 'Bomkalkulator', desc: 'Beregning av bomavgifter for alle norske bomstasjoner basert på kjøretøytype, drivstoff og rute.', color: '#3B6FD4' },
            { title: 'Interaktivt Veikart', desc: 'Sanntidsinformasjon om veiforhold, stengte veier, veiarbeid og trafikksituasjonen i Norge.', color: '#F97316' },
            { title: 'Kjøretøyadministrasjon', desc: 'Registrering og administrasjon av kjøretøy, AutoPASS-brikker og kjøretøyprofiler.', color: '#10B981' },
            { title: 'Abonnementsadministrasjon', desc: 'Administrasjon av abonnementer, fakturaer, betalingsmetoder og kontoinnstillinger.', color: '#8B5CF6' },
          ]?.map((item, i) => (
            <div
              key={i}
              style={{
                background: `${item?.color}08`,
                border: `1px solid ${item?.color}20`,
                borderTop: `3px solid ${item?.color}`,
                padding: '20px'
              }}
            >
              <div
                style={{
                  fontFamily: 'Syne, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: item?.color,
                  marginBottom: '8px'
                }}
              >
                {item?.title}
              </div>
              <div
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.8125rem',
                  color: 'rgba(238,240,242,0.65)',
                  lineHeight: 1.6
                }}
              >
                {item?.desc}
              </div>
            </div>
          ))}
        </div>

        <BodyText>
          NordPass forbeholder seg retten til å endre, oppdatere eller avvikle deler av tjenestene uten forhåndsvarsel, forutsatt at dette ikke vesentlig forringer den avtalte tjenesten for betalende abonnenter.
        </BodyText>
      </section>
    </>
  );
};

export default AcceptanceServiceSection;
